import { Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import Expo48Form, { type Expo48FormData } from "@/components/expo-48-form";
import { formatNumberWithCommas, stripCommasToNumber } from "@/lib/expo-48-constants";
import type { Expo48Data } from "@/server/db/expo-types";

interface PropertyPurchase {
  id: number;
  purchase_price: number;
  developer: string;
  full_address: string;
  purchased_property_type: string | null;
  purchased_property_characteristics: string | null;
  coupon_ready_at: string | null;
}

interface RegistrationItem {
  id: number;
  name: string;
  surname: string;
  phone: string;
  email: string;
  age: number | null;
  gender: string | null;
  uuid: string;
  expoData: Expo48Data;
  property_purchases?: PropertyPurchase[];
}

export default function EditRegistrationPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = params.id || "";
  const mode = searchParams.get("mode") || "edit";
  const isCouponMode = mode === "coupon";

  // Get token from navigation state
  const [token] = useState<string | null>((location.state as { token?: string })?.token || null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<RegistrationItem | null>(null);
  const [defaultValues, setDefaultValues] = useState<Partial<Expo48FormData> | undefined>(
    undefined
  );
  const [initialPurchaseIds, setInitialPurchaseIds] = useState<number[]>([]);

  useEffect(() => {
    document.title = "Edit Registration | House & Condo Expo";
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!id) return;

      // Redirect to lookup if no token
      if (!token) {
        navigate("/lookup", {
          state: { error: "กรุณายืนยันตัวตนก่อน / Please verify your identity first" },
        });
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`/api/registrations/${id}?token=${encodeURIComponent(token)}`);

        if (res.status === 401 || res.status === 403) {
          // Token invalid or expired
          navigate("/lookup", {
            state: {
              error:
                "หมดเวลายืนยันตัวตน กรุณาลองอีกครั้ง / Verification expired. Please try again.",
            },
          });
          return;
        }

        if (!res.ok) throw new Error("Not found");
        const data = (await res.json()) as { item: RegistrationItem };
        setItem(data.item);

        const purchases =
          data.item.property_purchases && data.item.property_purchases.length > 0
            ? data.item.property_purchases.map((pp) => ({
                id: pp.id as unknown as number,
                purchasePrice: formatNumberWithCommas(pp.purchase_price),
                developer: pp.developer || "",
                fullAddress: pp.full_address || "",
                purchasedPropertyType: pp.purchased_property_type || "",
                purchasedPropertyCharacteristics: pp.purchased_property_characteristics || "",
              }))
            : isCouponMode
              ? [
                  {
                    purchasePrice: "",
                    developer: "",
                    fullAddress: "",
                    purchasedPropertyType: "",
                    purchasedPropertyCharacteristics: "",
                  },
                ]
              : [];

        setDefaultValues({
          name: data.item.name,
          surname: data.item.surname,
          phone: data.item.phone,
          email: data.item.email,
          age: data.item.age?.toString() || "",
          gender: (data.item.gender as unknown as string) || "",
          interests: data.item.expoData.interests || [],
          interestOther: data.item.expoData.interestOther || "",
          banks: data.item.expoData.banks || [],
          bankOther: data.item.expoData.bankOther || "",
          propertyCharacteristics: data.item.expoData.propertyCharacteristics || "",
          priceRange: data.item.expoData.priceRange || "",
          locationData: data.item.expoData.locationData || { province: "", district: "" },
          purchaseYear: data.item.expoData.purchaseYear || "",
          purchases,
        });
        setInitialPurchaseIds(data.item.property_purchases?.map((pp) => pp.id) || []);
      } catch {
        setError("ไม่พบข้อมูล / Not found");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, token, navigate, isCouponMode]);

  const onSubmit = async (values: Expo48FormData) => {
    if (!item || !token) return;
    setSaving(true);
    try {
      // Prepare property purchases
      const property_purchases =
        values.purchases?.map((purchase) => ({
          id: purchase.id,
          purchasePrice: purchase.purchasePrice
            ? stripCommasToNumber(purchase.purchasePrice)
            : undefined,
          developer: purchase.developer,
          fullAddress: purchase.fullAddress,
          purchasedPropertyType: purchase.purchasedPropertyType,
          purchasedPropertyCharacteristics: purchase.purchasedPropertyCharacteristics,
        })) || [];

      const res = await fetch(
        `/api/registrations/${item.uuid}?token=${encodeURIComponent(token)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: values.name,
            surname: values.surname,
            phone: values.phone,
            email: values.email,
            age: values.age && values.age.trim() ? Number(values.age) : null,
            gender: values.gender || null,
            expoData: {
              interests: values.interests,
              interestOther: values.interestOther,
              banks: values.banks,
              bankOther: values.bankOther,
              propertyCharacteristics: values.propertyCharacteristics,
              priceRange: values.priceRange,
              locationData: values.locationData,
              purchaseYear: values.purchaseYear,
            },
            property_purchases,
            initialPurchaseIds,
          }),
        }
      );

      if (res.status === 401 || res.status === 403) {
        // Token invalid or expired during save
        navigate("/lookup", {
          state: {
            error: "หมดเวลายืนยันตัวตน กรุณาลองอีกครั้ง / Verification expired. Please try again.",
          },
        });
        return;
      }

      if (!res.ok) throw new Error("Failed to save");

      // Navigate to lookup with verified state to show choice view
      navigate("/lookup", {
        state: {
          verified: true,
          registrationUuid: item.uuid,
          verificationToken: token,
          verifiedName: item.name,
        },
      });
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getTitle = () => {
    if (isCouponMode) return "Register Purchase for Coupon / ลงทะเบียนซื้อเพื่อรับคูปอง";
    return "View & Edit Registration / ดูและแก้ไขข้อมูล";
  };

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex flex-col gap-3">
            <div>
              <Button variant="outline" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>
            <CardTitle className="text-2xl text-center">{getTitle()}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <div className="text-red-600 text-sm">{error}</div>
          ) : item && defaultValues ? (
            <Expo48Form
              mode="edit"
              requireCouponFields={isCouponMode}
              defaultValues={defaultValues}
              onSubmit={onSubmit}
            >
              <Button type="submit" disabled={saving} className="w-full">
                {saving
                  ? "Saving..."
                  : isCouponMode
                    ? "Save & Get Coupon"
                    : "Save Changes / บันทึก"}
              </Button>
            </Expo48Form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
